import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePublicMembershipEnabled } from "../components/PreLaunchGuard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import LanguageSelector from "../components/LanguageSelector";
import {
  Anchor, Mail, Lock, ArrowRight, Ship, Building,
  UtensilsCrossed, Handshake, Package, Star
} from "lucide-react";
import { toast } from "sonner";

export default function ComingSoonPage() {
  const { t } = useTranslation();
  const publicMembership = usePublicMembershipEnabled();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    const clean = (email || "").trim();
    if (!clean || busy) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: clean, source: "coming_soon" }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.status === "already_confirmed") {
          toast.success("Vous êtes déjà inscrit·e à la newsletter.");
        } else {
          toast.success(t("comingSoon.thankyouToast"));
        }
        setSubmitted(true);
        setEmail("");
      } else if (res.status === 429) {
        toast.error("Trop de tentatives. Merci de patienter quelques minutes.");
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Une erreur est survenue.");
      }
    } catch (err) {
      toast.error("Impossible de contacter le serveur.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary relative overflow-hidden" data-testid="coming-soon-page">
      {/* Sélecteur de langue public */}
      <div className="absolute top-5 right-5 z-20">
        <LanguageSelector variant="light" />
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10"><Ship className="h-32 w-32" /></div>
        <div className="absolute top-40 right-20"><Building className="h-24 w-24" /></div>
        <div className="absolute bottom-40 left-1/4"><UtensilsCrossed className="h-20 w-20" /></div>
        <div className="absolute bottom-20 right-1/3"><Anchor className="h-28 w-28" /></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16">
        <div className="max-w-3xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20 mb-6">
              <Anchor className="h-10 w-10 text-accent" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3">ITERCATUS</h1>
            <p className="text-accent text-base font-body">{t("common.tagline")}</p>
            <p className="text-white/40 text-xs mt-1 italic">{t("common.latin")}</p>
          </div>

          {/* Main Message */}
          <div className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-5" data-testid="coming-soon-title">
              {t("comingSoon.title")}
            </h2>
            <p className="text-white/70 text-lg font-body leading-relaxed max-w-2xl mx-auto">
              {t("comingSoon.description")}
            </p>
          </div>

          {/* Services Preview */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 max-w-2xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <Ship className="h-6 w-6 text-accent mx-auto mb-3" />
              <p className="text-white/80 text-sm font-medium">{t("comingSoon.svcExclusive")}</p>
              <p className="text-white/50 text-xs mt-1">{t("comingSoon.svcExclusiveDesc")}</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <Package className="h-6 w-6 text-accent mx-auto mb-3" />
              <p className="text-white/80 text-sm font-medium">{t("comingSoon.svcTests")}</p>
              <p className="text-white/50 text-xs mt-1">{t("comingSoon.svcTestsDesc")}</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <Star className="h-6 w-6 text-accent mx-auto mb-3" />
              <p className="text-white/80 text-sm font-medium">{t("comingSoon.svcStories")}</p>
              <p className="text-white/50 text-xs mt-1">{t("comingSoon.svcStoriesDesc")}</p>
            </div>
          </div>

          {/* Email Notification Form */}
          {!submitted ? (
            <form onSubmit={handleNotify} className="mb-10">
              <p className="text-white/60 text-sm mb-4">{t("comingSoon.notifyLabel")}</p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("comingSoon.emailPlaceholder")}
                    className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full"
                    required
                  />
                </div>
                <Button type="submit" disabled={busy} className="bg-accent hover:bg-accent/90 text-white px-8 py-6 rounded-full font-semibold" data-testid="coming-soon-subscribe-btn">
                  {t("comingSoon.notifyButton")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          ) : (
            <div className="mb-10 bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto border border-white/20">
              <p className="text-white font-medium">{t("comingSoon.thankyouBox")}</p>
            </div>
          )}

          {/* Partner Section — masquée quand `public_membership_enabled=false`
              (Phase I : promotion commerciale B2B retirée du mode éditorial public). */}
          {publicMembership && (
          <div className="bg-accent/10 backdrop-blur-sm rounded-2xl p-8 mb-10 border border-accent/20 max-w-xl mx-auto">
            <Handshake className="h-8 w-8 text-accent mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-white mb-3">{t("comingSoon.partnerTitle")}</h3>
            <p className="text-white/70 text-sm font-body leading-relaxed mb-5">{t("comingSoon.partnerText")}</p>
            <Link to="/entreprises">
              <Button className="bg-accent hover:bg-accent/90 text-white px-8 py-5 rounded-full font-semibold">
                {t("comingSoon.partnerButton")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          )}

          {/* Admin Access */}
          <div className="pt-6 border-t border-white/10">
            <p className="text-white/40 text-sm mb-3">{t("comingSoon.adminLabel")}</p>
            <Link to="/connexion">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6">
                <Lock className="mr-2 h-4 w-4" />
                {t("comingSoon.adminButton")}
              </Button>
            </Link>
          </div>
        </div>

        {/* Public association identity and official domains */}
        <footer className="mt-10 w-full max-w-3xl px-4 text-center" data-testid="coming-soon-association">
          <p className="text-white/80 text-sm font-body">{t("comingSoon.assoc")}</p>
          <p className="text-white/60 text-xs mt-2" lang="fr">RNA : W133042393</p>
          <p className="text-white/70 text-sm leading-relaxed mt-3" lang="fr">
            ITERCATUS est un projet porté par l’association à but non lucratif ITERCATUS.
          </p>
          <p className="text-white/70 text-sm leading-relaxed mt-2" lang="fr">
            <a href="https://itercatus.com" className="text-accent underline underline-offset-4">itercatus.com</a>
            {", "}
            <a href="https://itercatus.fr" className="text-accent underline underline-offset-4">itercatus.fr</a>
            {" et "}
            <a href="https://itercatus.eu" className="text-accent underline underline-offset-4">itercatus.eu</a>
            {" sont les domaines officiels utilisés par l’association ITERCATUS."}
          </p>
        </footer>
      </div>
    </div>
  );
}
